import styled from '@emotion/styled';
import React, { useCallback, useRef } from 'react';
import { format, isSameDay } from 'date-fns';
import { Overlay } from 'react-bootstrap';
import { useCurrentLocale } from '../../../../../context/I18n';
import { getDateFnsLocaleByString } from '../../../../../utils/dateUtils';
import Cell from './EmployeeAbsenceCalendarCardCell';
import { Nullable } from '../../../../../utils/nullable';
import { CalendarDay } from '../../AbsenceCalendar/types';
import { calcVariant, isOverlapped } from '../../AbsenceCalendar/Utils';
import AbsenceDisplay from '../../AbsenceCalendar/AbsenceDisplay';
import AbsenceOverlay from '../../AbsenceCalendar/AbsenceOverlay';
import HolidayPopover from '../../HolidayPopover';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';

type Props = {
  day: CalendarDay;
  onHover: (day: Nullable<CalendarDay>) => void;
  hoveredDay?: Nullable<CalendarDay>;
};

const Content = styled.div<{ visible: boolean }>(
  {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  ({ visible }) => ({
    visibility: visible ? 'visible' : 'hidden',
  }),
);

const EmployeeAbsenceCalendarCardDayCell: React.FC<Props> = ({
  day,
  onHover,
  hoveredDay,
}) => {
  const locale = useCurrentLocale();
  const dateFnsLocale = getDateFnsLocaleByString(locale);
  const target = useRef<HTMLTableDataCellElement>(null);

  const absences = day.absences;

  const absence = absences[0];
  const holiday = day.holiday;

  const dayDate = day.day.toDate();
  const isHovered = !!hoveredDay && isSameDay(hoveredDay.day.toDate(), dayDate);

  const handleMouseEnter = useCallback(() => {
    onHover(day);
  }, [day, onHover]);

  const overlappedAbsences = isOverlapped(absences);

  const variant = calcVariant(
    day.workday,
    overlappedAbsences,
    absences,
    holiday,
  );

  const today = isSameDay(new Date(), dayDate);

  const showVerticalLine =
    !!hoveredDay &&
    hoveredDay.day.date() === day.day.date() &&
    day.day.month() < hoveredDay.day.month();
  const showHorizontalLine =
    !!hoveredDay &&
    hoveredDay.day.month() === day.day.month() &&
    day.day.date() < hoveredDay.day.date();

  const weekdayName = format(dayDate, 'EEE', { locale: dateFnsLocale });

  return (
    <>
      <Cell
        ref={target}
        hover={day.hover}
        onMouseEnter={handleMouseEnter}
        cellHovered={isHovered}
        variant={variant}
        today={today}
        showVerticalLine={showVerticalLine}
        showHorizontalLine={showHorizontalLine}>
        {absences && (
          <AbsenceDisplay
            day={day}
            absences={absences}
            isHovered={isHovered || today}
          />
        )}
        <Content visible={isHovered || today}>
          {overlappedAbsences ? (
            <FlairIcon icon="alert-circle-outline" />
          ) : (
            <strong>{day.day.date()}</strong>
          )}
          <span>{weekdayName}</span>
        </Content>
      </Cell>

      {absence && (
        <AbsenceOverlay
          target={target}
          showPopover={isHovered}
          absence={absence}
          absences={absences}
          holiday={holiday}
        />
      )}

      {holiday && (
        <Overlay
          target={target.current}
          show={!absence && isHovered}
          placement="top">
          {(props) => <HolidayPopover holiday={holiday} {...props} />}
        </Overlay>
      )}
    </>
  );
};
export default EmployeeAbsenceCalendarCardDayCell;
